import { useRelationDialogOpen } from '@/components/RelationActionMenu/hooks/useRelationDialogOpen';
import { ActionMenus } from '@/features/ap_payees/components/DocInstructionMenu/ActionMenus';
import { ExportInstructionPdfDialog } from '@/features/ap_payees/components/DocInstructionMenu/ExportInstructionsPdf';
import { SendInstructionsEmailDialog } from '@/features/ap_payees/components/DocInstructionMenu/SendInstructionsEmail/SendInstructionsEmailDialog';
import { DocInstructionMenuProps } from '@/features/ap_payees/components/DocInstructionMenu/types';
import { ActionMenuProps } from '@moneyforward/ap-frontend-components';
import ErrorBoundary from 'antd/es/alert/ErrorBoundary';
import { FC, memo, useCallback, useMemo, useState } from 'react';

const InnerDocInstructionMenu: FC<DocInstructionMenuProps> = memo(
  ({
    onSendEmail,
    disabledButton,
    disabledSendmail,
    disabledExportPdf,
    payees,
    payeeQuery,
  }) => {
    const [openInstructionPdfDialog, setOpenInstructionPdfDialog] =
      useState(false);
    const [openSendEmailDialog, setOpenSendEmailDialog] = useState(false);

    const {
      open: _open,
      onOpen: _onOpen,
      onClose: _onClose,
    } = useRelationDialogOpen();

    const onOpenChange = useCallback(
      (_open: boolean) => {
        _open ? _onOpen() : _onClose();
      },
      [_onOpen, _onClose]
    );

    const dropDownProps: ActionMenuProps['dropDownProps'] = useMemo(
      () => ({
        open: _open,
        onOpenChange,
        destroyPopupOnHide: true,
      }),
      [_open, onOpenChange]
    );

    const handleOpenInstructionPdfDialog = useCallback(() => {
      setOpenInstructionPdfDialog(true);
      _onClose();
    }, [_onClose, setOpenInstructionPdfDialog]);

    const handleCloseInstructionPdfDialog = useCallback(() => {
      setOpenInstructionPdfDialog(false);
    }, [setOpenInstructionPdfDialog]);

    const handleOpenSendEmailDialog = useCallback(() => {
      setOpenSendEmailDialog(true);
      _onClose();
      if (onSendEmail) {
        onSendEmail();
      }
    }, [_onClose, setOpenSendEmailDialog, onSendEmail]);

    const handleCloseSendEmailDialog = useCallback(() => {
      setOpenSendEmailDialog(false);
    }, [setOpenSendEmailDialog]);

    return (
      <>
        <ActionMenus
          onSendEmail={handleOpenSendEmailDialog}
          disabledSendmail={disabledSendmail}
          disabledExportPdf={disabledExportPdf}
          isDisabled={disabledButton}
          handleOpenInstructionPdfDialog={handleOpenInstructionPdfDialog}
          dropDownProps={dropDownProps}
        />
        <ExportInstructionPdfDialog
          open={openInstructionPdfDialog}
          onClose={handleCloseInstructionPdfDialog}
          payees={payees}
          payeeQuery={payeeQuery}
        />
        <SendInstructionsEmailDialog
          open={openSendEmailDialog}
          onClose={handleCloseSendEmailDialog}
          payees={payees}
          payeeQuery={payeeQuery}
        />
      </>
    );
  }
);

InnerDocInstructionMenu.displayName = 'InnerDocInstructionMenu';

export const DocInstructionMenu: FC<DocInstructionMenuProps> = memo(
  ({
    disabledButton,
    disabledSendmail,
    disabledExportPdf,
    onSendEmail,
    payees,
    payeeQuery,
  }) => {
    return (
      <ErrorBoundary>
        <InnerDocInstructionMenu
          onSendEmail={onSendEmail}
          disabledButton={disabledButton}
          disabledSendmail={disabledSendmail}
          disabledExportPdf={disabledExportPdf}
          payees={payees}
          payeeQuery={payeeQuery}
        />
      </ErrorBoundary>
    );
  }
);

DocInstructionMenu.displayName = 'DocInstructionMenu';
